import angular from 'angular';
import { Subject } from 'rxjs';
import { ConfigurationManagerService } from 'src/app/core';

import {
    CashdrawerDriver,
    CashdrawerDriverCommand,
    CashdrawerStatus
} from 'src/app/shared/model/cash-drawer.model';

import {
    PaymentMethods,
    SalesPayments
} from 'tilby-models';

class CashdrawerPaymentDialogController {
    private driverController?: Subject<CashdrawerDriverCommand>;
    protected trSt?: CashdrawerStatus;
    protected message?: string;
    protected inProgress = false;

    protected closePreviousTransaction?: () => void;
    protected confirm?: () => void;

    static $inject = [
        "$mdDialog",
        "$translate",
        "amount",
        "paymentMethod",
        "cashdrawerDriver",
        "cashdrawerName",
        "transactionType"
    ];

    constructor(
        private $mdDialog: any,
        private $translate: any, 
        private amount: number, 
        paymentMethod: PaymentMethods, 
        private cashdrawerDriver: CashdrawerDriver, 
        protected cashdrawerName: string,
        protected transactionType?: string
    ) {
        this.startTransaction(amount, paymentMethod);
    }

    cancel () {
        if (!this.trSt) {
            this.$mdDialog.cancel('CANCELED');
        } else if (this.trSt.paid >= this.amount) {
            this.$mdDialog.hide(this.trSt);
        } else {
            this.driverController?.next('ABORT_TRANSACTION');
        }
    };

    getToPay() {
        return Math.abs(this.trSt?.toPay || 0);
    }

    private async startTransaction(amount: number, paymentMethod: PaymentMethods) {
        this.message = this.$translate.instant('DIGITAL_PAYMENTS.CASHDRAWERS.CONNECTING', {drawer: this.cashdrawerName});
        this.inProgress = true;

        //Start payment transaction
        let driverInterface = this.cashdrawerDriver.performPayment(amount, paymentMethod.schema_name!, paymentMethod.bundle_name);

        //Link the driver controller subject to the dialog
        this.driverController = driverInterface.driverController;

        //Subscribe to transaction status observable
        driverInterface.transactionStatus.subscribe({
            //Transaction status update
            next: (trSt: CashdrawerStatus) => {
                //Remove 'connecting' message and loader on first response
                if (!this.trSt) {
                    this.message = undefined;
                    this.inProgress = false;
                    delete this.closePreviousTransaction;
                }

                //Transaction is (being) canceled
                if (trSt.canceled) {
                    this.message = this.$translate.instant('DIGITAL_PAYMENTS.CASHDRAWERS.ABORTING');
                    this.inProgress = true;
                } else if (trSt.transactionStatus === 'BUSY') {
                    this.message = this.$translate.instant('DIGITAL_PAYMENTS.CASHDRAWERS.ANOTHER_OPEN_TRANSACTION', {drawer: this.cashdrawerName});
                    this.inProgress = true;

                    if (driverInterface.allowsClosingPendingTransactions) {
                        this.closePreviousTransaction = () => {
                            //Send transaction abort command to the driver
                            this.driverController?.next('ABORT_TRANSACTION');

                            this.message = this.$translate.instant('DIGITAL_PAYMENTS.CASHDRAWERS.FORCING');

                            //Remove this function and the related button
                            delete this.closePreviousTransaction;
                        };
                    }
                } else {
                    this.trSt = trSt;

                    if (trSt.message) {
                        this.message = this.$translate.instant(trSt.message);
                    }
                }
            },
            //Transaction error
            error: (error: string) => {
                if (error === 'REQ_TIMEOUT' && !this.trSt) {
                    error = 'FIRST_REQ_TIMEOUT';
                }

                this.$mdDialog.cancel(error);
            },
            //Transaction completion
            complete: () => {
                const finalStatus = this.trSt!;

                if (finalStatus.canceled || finalStatus.paid < finalStatus.toPay) { //Payment not successful (Aborted)
                    this.$mdDialog.cancel('CANCELED');
                } else if (finalStatus.changeNotGiven && this.transactionType !== 'DEPOSIT') { //Payment successful but the drawer couldn't give the correct change
                    this.message = this.$translate.instant('DIGITAL_PAYMENTS.CASHDRAWERS.CHANGE_NOT_GIVEN_ERROR');

                    //Expose confirm method to the dialog UI
                    this.confirm = () => {
                        this.$mdDialog.hide(finalStatus);
                    }
                } else { //Payment successful
                    this.$mdDialog.hide(finalStatus);
                }
            }
        });
    }
}

export class CashdrawerPaymentDialog {
    static $inject = [
        "$mdDialog",
        "$translate",
        "checkManager"
    ];

    constructor(
        private $mdDialog: any,
        private $translate: any,
        private configurationManager: ConfigurationManagerService
    ) {
    }

    public async show(amount: number, paymentMethod: PaymentMethods, cashdrawerDriver: CashdrawerDriver, cashdrawerName: string, transactionType?: string) {
        if (!paymentMethod.schema_name) {
            throw this.$translate.instant('DIGITAL_PAYMENTS.CASHDRAWERS.SCHEMA_NAME_NOT_FOUND', {drawer: cashdrawerName});
        }

        return this.$mdDialog.show({
            controller: CashdrawerPaymentDialogController,
            template: require('./cashdrawer-payment-dialog.html'),
            locals: {
                amount: amount,
                paymentMethod: structuredClone(paymentMethod),
                cashdrawerDriver: cashdrawerDriver,
                cashdrawerName: cashdrawerName,
                transactionType: transactionType
            },
            controllerAs: '$ctrl'
        }).then((result: CashdrawerStatus) => {
            const { message, canceled, ...transaction } = result;

            let amountOverride;

            if(this.configurationManager.getPreference('cashregister.register_cashdrawers_paid_amount')) {
                amountOverride = transaction.paid;

                if(amount < 0 && amountOverride > 0) {
                    amountOverride = -amountOverride;
                }
            }

            return <Partial<SalesPayments>>{
                ...(amountOverride ? { amount: amountOverride } : {}),
                payment_data: JSON.stringify(transaction),
                unclaimed: false,
            };
        })
    }
}

angular.module('digitalPayments').service('CashdrawerPaymentDialog', CashdrawerPaymentDialog);
